import React, {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { MAP_ATTRIBUTION_GEOCOMM } from '@rsos/capstone/src/constants/capabilities';
import { withLayout } from '@rsos/components/capstone/base/Layout';
import DeckGLNext from '@rsos/components/capstone/common/Map/useDeckGL/DeckGLNext';
import { EMERGENCY_CALLS } from '@rsos/constants/incidentTypes';
import { initializeMap } from '@rsos/maps';
import { normalizeExtent } from '@rsos/maps/utils/utils';
import { filteredAlertsSelector } from '@rsos/reduxDataStore/src/features/selectors/alertsSelectors';
import checkEsriMapAccess from '@rsos/utils/checkEsriMapAccess';
import checkGISServicesAccess from '@rsos/utils/checkGISServicesAccess';
import exportToGlobalNamespace from '@rsos/utils/exportToGlobalNamespace';
import usePortalType from '@rsos/utils/usePortalType';
import useEsriMapTransition from '../../../hooks/useEsriMapTransition';
import useEsriSketchWidget from '../../../hooks/useEsriSketchWidget';
import useGISBasemapLayer from '../../../hooks/useGISBasemapLayer';
import useGISServiceLayers from '../../../hooks/useGISServiceLayers';
import { getGISLayerID } from '../../../utils/getGISLayerID';
import { getMostRecentLoc } from '../../../utils/getMostRecentLoc';
import MapCard from '../../MapCard';
import { EsriMapsContext } from './EsriMapsProvider';
import GeoCommIndoorMaps from './GeoCommIndoorMaps';
import useDrawERA from './hooks/useDrawEra';
import useGISToken from './hooks/useGISToken';
import { W3WMarkers } from './markers/W3WMarkers';
import AllAlertMarkers from './markers/alerts/AllAlertMarkers';
import { CallMarkers } from './markers/calls/CallMarkers';
import { getMapsApiKey, updateAttributionText } from './utils';

const MapWrapper = withLayout(styled.div`
  height: 100%;
  width: 100%;
`);

const StyledMap = styled.div`
  width: 100%;
  height: 100%;
`;

const EsriDarkTheme = lazy(() =>
  import('@rsos/maps/providers/esri/themes/DarkTheme'),
);

/**
 * @param {String} activeQueueTab - ALERTS or EMERGENCY_CALLS
 * @param {Object} currentERA - ERA data
 * @param {String} hoveredCall - Phone number of the hovered call
 * @param {Function} onMapReady - Callback function when Map is ready for JV
 * @param {Function} onMarkerAdd - Callback function when a new marker is drawn
 * @param {Function} onMarkerClick - onClick handler for markers
 * @param {Function} onMarkerHover - Hover effect handler for markers
 * @param {Function} onRemoveID - Function to set state in JV for removed alert
 * @param {Function} setMafError - Function to set medicalert error
 * @param {Function} setADRError - Function to set adr error
 * @param {String} themeName - Theme name used to switch Google maps theme
 */

export const Map = ({
  activeQueueTab,
  currentERA,
  hoveredCall,
  onMapReady,
  onMarkerAdd,
  onMarkerClick,
  onMarkerHover,
  onRemoveID,
  removedID,
  setMafError,
  setADRError,
  themeName,
  triggerViewJurisdiction,
}) => {
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);

  const { exact_polygon_center, shapes = [] } = useSelector(
    state => state.geofences.currentGeofence,
  );

  const { isPremiumUser } = usePortalType();

  const alerts = useSelector(state => filteredAlertsSelector(state.incidents));

  const selectedAlertID = useSelector(state => state.incidents.selectedAlertID);

  const { hoveredAlertID, hoveredFrom } = useSelector(
    state => state.hoveredIncident,
  );

  const hoveredAlert = alerts[hoveredAlertID];

  const callQueue = useSelector(state => state.emergencyCalls.callQueue);

  const selectedCallerID = useSelector(
    state => state.emergencyCalls.selectedCallerID,
  );

  const hasLocation = getMostRecentLoc(callQueue[selectedCallerID]);

  const isAllView = useMemo(() => {
    return !hasLocation && !selectedAlertID;
  }, [hasLocation, selectedAlertID]);

  const hasW3W = useSelector(state => state.what3words.searched?.coordinates);

  const capabilities = useSelector(
    state => state.psaps.currentPSAP.activeCapabilities,
  );

  const gisServices = useSelector(state => state.gisServices.gisServices);

  const activeGISServices = useSelector(
    state => state.gisServices.activeGISServices,
  );

  const lastToggledGISService = useSelector(
    state => state.gisServices.lastToggledGISService,
  );

  const hasEsriMapAccess =
    useSelector(state =>
      checkEsriMapAccess({
        psaps: state.psaps,
        sinatra: state.sinatra,
      }),
    ) && isPremiumUser;

  const hasGISServicesAccess =
    useSelector(state =>
      checkGISServicesAccess({
        psaps: state.psaps,
        sinatra: state.sinatra,
      }),
    ) && hasEsriMapAccess;

  const mapProvider = useSelector(state => state.sinatra.ui.mapProvider);

  const basemap = useSelector(state => state.sinatra.ui.basemap);

  const mapApiKey = getMapsApiKey(mapProvider);

  const loadMap = useCallback(() => {
    initializeMap({
      mapProvider,
      apiKey: mapApiKey,
      mapOptions: {
        mapContainer: mapRef.current,
        center: {
          lat: exact_polygon_center[1],
          lng: exact_polygon_center[0],
        },
        shapes: shapes,
        basemap: basemap,
      },
      viewOptions: {
        popup: {
          actions: [],
          autoOpenEnabled: false,
          dockEnabled: false,
          dockOptions: {
            buttonEnabled: false,
            breakpoint: false,
          },
          maxInlineActions: 0,
        },
      },
    }).then(map => {
      exportToGlobalNamespace('map', map);
      setMap(map);

      const geocommAttribution = capabilities[MAP_ATTRIBUTION_GEOCOMM];
      // check geocomm capability
      if (geocommAttribution && gisServices.length && hasGISServicesAccess) {
        updateAttributionText({ map, partnerText: 'GeoComm' });
      }
      // Move default zoom buttons to bottom right
      map.mapInstance.view.ui.move(['zoom'], 'bottom-right');
      onMapReady();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (mapRef.current) {
      loadMap();
    }
  }, [loadMap, mapRef]);

  // Reset the map viewport back to the jurisdiction area when going from 911 event detail view
  // back to all events view.
  useEffect(() => {
    if (map?.mapInstance && isAllView && !hasW3W) {
      map.mapInstance.view.extent = normalizeExtent(shapes);
    }
  }, [hasW3W, isAllView, map?.mapInstance, shapes, triggerViewJurisdiction]);

  const handleCallMarkerAdd = () => {
    onMarkerAdd(EMERGENCY_CALLS);
  };

  const handleCallMarkerClick = (callerID, isAllView) => {
    onMarkerClick(callerID, EMERGENCY_CALLS, isAllView);
  };

  const handleCallMarkerHover = (callerID, secondary) => {
    onMarkerHover(callerID, 'map', EMERGENCY_CALLS, secondary);
  };

  const handleCallMarkerRemove = callerID => {
    onRemoveID(callerID);
  };

  useDrawERA(map, currentERA);
  useGISToken();
  const gisBasemapLayer = useGISBasemapLayer(map);
  const gisServiceLayers = useGISServiceLayers(map);
  useEsriMapTransition(map);
  useEsriSketchWidget(map);

  // A combination of GIS cached-basemap and GIS supplemental layers after it has been created.
  const combinedGISLayers = useMemo(() => {
    const layers = [];

    if (gisBasemapLayer) layers.push(gisBasemapLayer);

    if (gisServiceLayers) layers.push(...gisServiceLayers);

    return layers;
  }, [gisBasemapLayer, gisServiceLayers]);

  // Add all of the GIS service layers to the map at once to ensure layers are added according to
  // the display_order/layerIndex property.
  useEffect(() => {
    if (hasGISServicesAccess && map?.mapInstance && combinedGISLayers.length) {
      combinedGISLayers.forEach(combinedLayer => {
        const { layer, service } = combinedLayer;

        const doesLayerExist = !!map.mapInstance.map.findLayerById(
          getGISLayerID(service),
        );

        // Check if the layer exists before adding to prevent duplicate layers.
        if (!doesLayerExist) map.mapInstance.map.add(layer, service.layerIndex);
      });
    }
  }, [hasGISServicesAccess, map?.mapInstance, combinedGISLayers]);

  // On page load, if any GIS service layers are checked in the map layers menu control, set the
  // visible property to true.
  useEffect(() => {
    if (
      hasGISServicesAccess &&
      map?.mapInstance &&
      activeGISServices.length &&
      combinedGISLayers.length &&
      !Object.keys(lastToggledGISService).length
    ) {
      activeGISServices.forEach(service => {
        const layerID = getGISLayerID(service);

        const layer = map.mapInstance.map.findLayerById(layerID);

        if (layer && !layer.visible) {
          layer.visible = true;
        }
      });
    }
  }, [
    hasGISServicesAccess,
    map?.mapInstance,
    activeGISServices,
    combinedGISLayers,
    lastToggledGISService,
  ]);

  return (
    <EsriMapsContext.Provider value={map}>
      <MapCard
        hasEsriMapAccess={false}
        mapRef={mapRef}
        setMafError={setMafError}
        setADRError={setADRError}
      />
      <MapWrapper>
        <StyledMap ref={mapRef}>
          {map?.mapInstance && (
            <>
              <W3WMarkers map={map} />
              <AllAlertMarkers
                activeQueueTab={activeQueueTab}
                hoveredAlert={hoveredAlert}
                hoveredFrom={hoveredFrom}
                map={map}
                onMarkerAdd={onMarkerAdd}
                onMarkerHover={onMarkerHover}
                onMarkerClick={onMarkerClick}
                onRemoveID={onRemoveID}
                removedID={removedID}
              />
              <CallMarkers
                activeQueueTab={activeQueueTab}
                hoveredCall={hoveredCall}
                hoveredFrom={hoveredFrom}
                map={map}
                onMarkerAdd={handleCallMarkerAdd}
                onMarkerClick={handleCallMarkerClick}
                onMarkerHover={handleCallMarkerHover}
                onRemoveID={handleCallMarkerRemove}
                removedID={removedID}
              />
              <DeckGLNext
                map={map}
                removedID={removedID}
                activeQueueTab={activeQueueTab}
              />

              <GeoCommIndoorMaps map={map} />
              {themeName === 'dark' && (
                <Suspense fallback={<></>}>
                  <EsriDarkTheme />
                </Suspense>
              )}
            </>
          )}
        </StyledMap>
      </MapWrapper>
    </EsriMapsContext.Provider>
  );
};
